import React, { useEffect, useMemo, useState } from "react";
import { useParams, useSearchParams, Link, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import Seo from "@/components/Seo";
import DoctorCard from "@/components/DoctorCard";
import { Search, SlidersHorizontal, X } from "lucide-react";

export default function SearchPage() {
  const { city = "pune", area: areaParam, specialty: specialtyParam } = useParams();
  const [params, setParams] = useSearchParams();
  const navigate = useNavigate();

  const [doctors, setDoctors] = useState([]);
  const [specialties, setSpecialties] = useState([]);
  const [areas, setAreas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showFilters, setShowFilters] = useState(false);

  // filters
  const [q, setQ] = useState(params.get("q") || "");
  const [gender, setGender] = useState(params.get("gender") || "");
  const [maxFee, setMaxFee] = useState(params.get("max_fee") || "");
  const [minExp, setMinExp] = useState(params.get("min_experience") || "");
  const [verifiedOnly, setVerifiedOnly] = useState(params.get("verified") === "1");

  useEffect(() => {
    api.get("/specialties").then((r) => setSpecialties(r.data.items || []));
    api.get(`/cities/${city}/areas`).then((r) => setAreas(r.data.items || []));
  }, [city]);

  // Determine if the "specialty" route param is actually an area (because we use /doctors/pune/:specialty too).
  // We only pass it as specialty if it matches a known specialty slug; otherwise treat as plain filter.
  const specialtySlug = useMemo(() => {
    if (!specialtyParam) return "";
    if (specialties.length === 0) return specialtyParam;
    return specialties.some((s) => s.slug === specialtyParam) ? specialtyParam : "";
  }, [specialtyParam, specialties]);

  const fetchDoctors = () => {
    setLoading(true);
    const p = {
      city,
      area: areaParam || undefined,
      specialty: specialtySlug || undefined,
      query: q || undefined,
      gender: gender || undefined,
      max_fee: maxFee || undefined,
      min_experience: minExp || undefined,
      verified_only: verifiedOnly || undefined,
      limit: 50,
    };
    api.get("/doctors", { params: p })
      .then((r) => setDoctors(r.data.items || []))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    fetchDoctors();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [city, areaParam, specialtySlug]);

  const applyFilters = (e) => {
    e?.preventDefault?.();
    const next = new URLSearchParams();
    if (q) next.set("q", q);
    if (gender) next.set("gender", gender);
    if (maxFee) next.set("max_fee", maxFee);
    if (minExp) next.set("min_experience", minExp);
    if (verifiedOnly) next.set("verified", "1");
    setParams(next);
    fetchDoctors();
    setShowFilters(false);
  };

  const resetFilters = () => {
    setQ(""); setGender(""); setMaxFee(""); setMinExp(""); setVerifiedOnly(false);
    setParams(new URLSearchParams());
    setTimeout(fetchDoctors, 0);
  };

  const title = useMemo(() => {
    const parts = [];
    if (specialtySlug) parts.push(specialties.find((s) => s.slug === specialtySlug)?.name || specialtySlug);
    parts.push("Doctors in");
    if (areaParam) parts.push(areas.find((a) => a.slug === areaParam)?.name || areaParam);
    parts.push(city.charAt(0).toUpperCase() + city.slice(1));
    return parts.join(" ");
  }, [specialtySlug, specialties, areaParam, areas, city]);

  return (
    <>
      <Seo
        title={title}
        description={`Browse ${doctors.length || "trusted"} doctors in ${city.toUpperCase()} on LocalDoctor.in. Filter by consultation fee, experience, gender and verified status.`}
      />

      <section className="border-b border-border bg-secondary/50">
        <div className="mx-auto max-w-[1280px] px-4 lg:px-6 py-8 lg:py-12">
          <nav className="text-xs text-muted-foreground mb-3">
            <Link to="/" className="hover:text-foreground">Home</Link> <span className="mx-1">/</span>
            <Link to={`/doctors/${city}`} className="hover:text-foreground">Pune</Link>
            {areaParam && <> <span className="mx-1">/</span> <span className="capitalize">{areaParam.replace(/-/g, " ")}</span></>}
            {specialtySlug && <> <span className="mx-1">/</span> <span className="capitalize">{specialtySlug.replace(/-/g, " ")}</span></>}
          </nav>
          <h1 className="font-display text-3xl md:text-4xl font-bold text-balance">{title}</h1>
          <p className="text-muted-foreground mt-2">Compare profiles, fees and availability — then book your OTP slot in seconds.</p>

          <form onSubmit={applyFilters} className="mt-6 flex flex-wrap gap-2 items-center">
            <div className="flex items-center gap-2 flex-1 min-w-[200px] bg-white border border-input rounded-lg px-3">
              <Search size={16} className="text-muted-foreground" />
              <input
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder="Search by name, clinic or specialty"
                className="w-full bg-transparent py-2.5 outline-none"
                data-testid="search-input"
              />
            </div>
            <select
              value={specialtySlug}
              onChange={(e) => {
                const slug = e.target.value;
                navigate(areaParam ? `/doctors/${city}/${slug || "area/" + areaParam}` : (slug ? `/doctors/${city}/${slug}` : `/doctors/${city}`));
              }}
              className="rounded-lg border border-input bg-white px-3 py-2.5"
              data-testid="filter-specialty"
            >
              <option value="">All specialties</option>
              {specialties.map((s) => <option key={s.slug} value={s.slug}>{s.name}</option>)}
            </select>
            <select
              value={areaParam || ""}
              onChange={(e) => {
                const slug = e.target.value;
                if (!slug) navigate(specialtySlug ? `/doctors/${city}/${specialtySlug}` : `/doctors/${city}`);
                else navigate(`/doctors/${city}/area/${slug}`);
              }}
              className="rounded-lg border border-input bg-white px-3 py-2.5"
              data-testid="filter-area"
            >
              <option value="">All areas</option>
              {areas.map((a) => <option key={a.slug} value={a.slug}>{a.name}</option>)}
            </select>
            <button type="button" onClick={() => setShowFilters(true)} className="btn-secondary" data-testid="open-filters">
              <SlidersHorizontal size={16} /> More filters
            </button>
            <button type="submit" className="btn-primary" data-testid="apply-filters">Apply</button>
          </form>
        </div>
      </section>

      <section className="mx-auto max-w-[1280px] px-4 lg:px-6 py-8">
        {loading ? (
          <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
            {Array.from({ length: 6 }).map((_, i) => (
              <div key={i} className="h-48 rounded-xl bg-muted/50 animate-pulse" />
            ))}
          </div>
        ) : doctors.length === 0 ? (
          <div className="text-center py-16" data-testid="no-results">
            <p className="font-display text-xl font-semibold">No doctors match your filters.</p>
            <p className="text-muted-foreground mt-2">Try broadening your search or clearing filters.</p>
            <button className="btn-primary mt-6" onClick={resetFilters}>Clear filters</button>
          </div>
        ) : (
          <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3" data-testid="doctor-list">
            {doctors.map((d) => <DoctorCard key={d.id} doctor={d} />)}
          </div>
        )}
      </section>

      {showFilters && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-end md:items-center md:justify-center" onClick={() => setShowFilters(false)}>
          <div className="bg-white w-full md:w-[480px] rounded-t-2xl md:rounded-2xl p-6" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-display text-lg font-semibold">More filters</h3>
              <button onClick={() => setShowFilters(false)} className="h-9 w-9 rounded-lg hover:bg-muted grid place-items-center" aria-label="Close">
                <X size={18} />
              </button>
            </div>
            <form onSubmit={applyFilters} className="grid gap-4">
              <label className="text-sm">
                <span className="font-medium">Gender</span>
                <select value={gender} onChange={(e) => setGender(e.target.value)} className="input-base mt-1" data-testid="filter-gender">
                  <option value="">Any</option>
                  <option value="female">Female</option>
                  <option value="male">Male</option>
                </select>
              </label>
              <label className="text-sm">
                <span className="font-medium">Max consultation fee (₹)</span>
                <input type="number" min="0" value={maxFee} onChange={(e) => setMaxFee(e.target.value)} className="input-base mt-1 tabular" placeholder="e.g. 800" data-testid="filter-max-fee" />
              </label>
              <label className="text-sm">
                <span className="font-medium">Minimum experience (years)</span>
                <input type="number" min="0" value={minExp} onChange={(e) => setMinExp(e.target.value)} className="input-base mt-1 tabular" placeholder="e.g. 5" data-testid="filter-min-exp" />
              </label>
              <label className="flex items-center gap-2 text-sm">
                <input type="checkbox" checked={verifiedOnly} onChange={(e) => setVerifiedOnly(e.target.checked)} data-testid="filter-verified" />
                Verified doctors only
              </label>
              <div className="flex gap-2 pt-2">
                <button type="button" onClick={resetFilters} className="btn-secondary flex-1">Reset</button>
                <button type="submit" className="btn-primary flex-1">Apply filters</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </>
  );
}
